import type {
  EchoflicksSessionJourney,
  I2VPromptAttempt,
  I2VPromptAuthoring,
  I2VPromptAuditSummary,
} from "../types";

export type I2VSceneDiagnostics = {
  sceneId: string;
  position: number | null;
  sourceFile: string | null;
  routeRole: "primary" | "secondary" | "unknown";
  status:
    | "accepted"
    | "retried"
    | "fallback"
    | "static"
    | "missing"
    | "pending";
  authorings: I2VPromptAuthoring[];
  activeAuthoring: I2VPromptAuthoring | null;
  missingAuthoringAuditIds: string[];
  reconciledAuthoringAuditIds: string[];
  attempts: I2VPromptAttempt[];
  providerAttemptCount: number;
  finalModel: string | null;
  finalProvider: string | null;
  whyMotion: string | null;
  authoredPrompt: string | null;
  effectivePromptHash: string | null;
  correlation: string | null;
  sceneRevisionId: string | null;
  promptRevisionId: string | null;
  configReleaseId: string | null;
  configReleaseDigest: string | null;
  routeContractVersion: string | null;
  routeContractDigest: string | null;
  promptBundleId: string | null;
  promptBundleDigest: string | null;
  templateId: string | null;
  templateVersion: string | null;
  templateDigest: string | null;
  packetId: string | null;
  packetDigest: string | null;
  providerRequestDigest: string | null;
  renderSpecFingerprint: string | null;
  requestedModel: string | null;
  reportedModel: string | null;
  modelMismatch: boolean;
  retryReason: string | null;
  fallbackReason: string | null;
  reuseStatus: string | null;
  reuseReason: string | null;
  transformationRecords: Record<string, unknown>[];
  capabilityWarnings: Record<string, unknown>[];
  attemptLineage: Array<{
    auditId: string;
    status: string;
    semanticAttemptOrdinal: number | null;
    transportTryIndex: number | null;
    parentAttemptId: string | null;
    patchId: string | null;
    provider: string | null;
    requestedModel: string | null;
    reportedModel: string | null;
    providerRequestDigest: string | null;
    idempotencyKey: string | null;
    correctionValidation: I2VPromptAuditSummary["correction_validation"];
  }>;
};

export type I2VDiagnosticsModel = {
  telemetryState:
    | "available"
    | "partial"
    | "unavailable_legacy"
    | "excluded_by_policy"
    | "projection_degraded";
  status: string;
  authoringMode: string | null;
  eligibleSceneCount: number;
  attachedSceneCount: number | null;
  authoredSceneCount: number;
  directedSceneCount: number;
  customPromptSceneCount: number;
  primarySceneCount: number;
  secondarySceneCount: number;
  acceptedSceneCount: number;
  retriedSceneCount: number;
  fallbackSceneCount: number;
  providerAttemptCount: number;
  authoringProjectionGapCount: number;
  visionDigest: string | null;
  shotListDigest: string | null;
  scenes: I2VSceneDiagnostics[];
};

function isEmptyCollection(value: unknown) {
  return (
    (Array.isArray(value) && value.length === 0) ||
    (typeof value === "object" &&
      value !== null &&
      !Array.isArray(value) &&
      Object.keys(value).length === 0)
  );
}

/** Sparse lifecycle rows may enrich but must not null-out richer prompt provenance. */
export function mergeDefinedSummary<T extends Record<string, unknown>>(
  existing: T,
  incoming: Partial<T>,
): T {
  const merged = { ...existing };
  for (const [key, value] of Object.entries(incoming)) {
    if (
      value === undefined ||
      value === null ||
      value === "" ||
      isEmptyCollection(value)
    ) {
      continue;
    }
    (merged as Record<string, unknown>)[key] = value;
  }
  return merged;
}

function sceneKey(value: string | number | null | undefined) {
  return value == null ? "unknown" : String(value);
}

function timestamp(value: I2VPromptAuthoring) {
  return value.updated_at ?? value.occurred_at ?? "";
}

function authoringRank(value: I2VPromptAuthoring) {
  if (value.status === "authored") return 4;
  if (value.status === "copied_legacy") return 3;
  if (value.status === "pending") return 2;
  return 1;
}

function selectLatestAuthoring(values: I2VPromptAuthoring[]) {
  return (
    [...values].sort((left, right) => {
      const rank = authoringRank(right) - authoringRank(left);
      if (rank) return rank;
      const time = timestamp(right).localeCompare(timestamp(left));
      if (time) return time;
      return right.audit_id.localeCompare(left.audit_id);
    })[0] ?? null
  );
}

function attemptStatus(attempt: I2VPromptAttempt) {
  return attempt.status ?? attempt.prompt_audit_summary?.lifecycle_status ?? "";
}

function isAccepted(attempt: I2VPromptAttempt) {
  return (
    [
      "succeeded",
      "uploaded",
      "accepted",
      "fallback_selected",
      "reused",
    ].includes(attemptStatus(attempt)) ||
    attempt.prompt_audit_summary?.asset_disposition === "accepted"
  );
}

function finalAttempt(attempts: I2VPromptAttempt[]) {
  return (
    [...attempts].sort((left, right) => {
      const accepted = Number(isAccepted(right)) - Number(isAccepted(left));
      if (accepted) return accepted;
      const terminal =
        Number(Boolean(right.terminal)) - Number(Boolean(left.terminal));
      if (terminal) return terminal;
      return (right.attempt ?? 0) - (left.attempt ?? 0);
    })[0] ?? null
  );
}

function decisionText(authoring: I2VPromptAuthoring | null) {
  const decision = authoring?.decision;
  if (!decision) return null;
  for (const key of ["operator_summary", "continuity_cue", "camera_move"]) {
    const value = decision[key];
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return null;
}

export function buildI2VDiagnostics(
  journey: EchoflicksSessionJourney | null | undefined,
): I2VDiagnosticsModel {
  const authoringsByScene = new Map<string, Map<string, I2VPromptAuthoring>>();
  for (const authoring of journey?.i2v_prompt_authorings ?? []) {
    const sceneId = sceneKey(authoring.scene_id);
    const values = authoringsByScene.get(sceneId) ?? new Map();
    const existing = values.get(authoring.audit_id);
    values.set(
      authoring.audit_id,
      existing
        ? (mergeDefinedSummary(
            existing as unknown as Record<string, unknown>,
            authoring as unknown as Record<string, unknown>,
          ) as unknown as I2VPromptAuthoring)
        : authoring,
    );
    authoringsByScene.set(sceneId, values);
  }

  const attemptsByScene = new Map<string, Map<string, I2VPromptAttempt>>();
  for (const attempt of journey?.i2v_prompt_attempts ?? []) {
    const sceneId = sceneKey(
      attempt.scene_id ?? attempt.prompt_audit_summary?.scene_id,
    );
    const values = attemptsByScene.get(sceneId) ?? new Map();
    const existing = values.get(attempt.audit_id);
    values.set(
      attempt.audit_id,
      existing
        ? ({
            ...existing,
            ...attempt,
            prompt_audit_summary: mergeDefinedSummary(
              (existing.prompt_audit_summary ?? {}) as Record<string, unknown>,
              (attempt.prompt_audit_summary ?? {}) as Record<string, unknown>,
            ) as I2VPromptAuditSummary,
          } satisfies I2VPromptAttempt)
        : attempt,
    );
    attemptsByScene.set(sceneId, values);
  }

  const sceneIds = new Set([
    ...authoringsByScene.keys(),
    ...attemptsByScene.keys(),
  ]);
  const projectedAuthoringIds = new Set(
    [...authoringsByScene.values()].flatMap((values) => [...values.keys()]),
  );
  sceneIds.delete("unknown");
  const scenes = [...sceneIds]
    .map((sceneId): I2VSceneDiagnostics => {
      const authorings = [
        ...(authoringsByScene.get(sceneId)?.values() ?? []),
      ].sort((left, right) => left.audit_id.localeCompare(right.audit_id));
      const activeAuthoring = selectLatestAuthoring(authorings);
      const linkedAttempts = [
        ...(attemptsByScene.get(sceneId)?.values() ?? []),
      ].sort(
        (left, right) =>
          (left.attempt ?? 0) - (right.attempt ?? 0) ||
          left.audit_id.localeCompare(right.audit_id),
      );
      const missingAuthoringAuditIds = [
        ...new Set(
          linkedAttempts.flatMap((attempt) => {
            const auditId =
              attempt.prompt_audit_summary?.authoring_audit_id?.trim();
            return auditId && !projectedAuthoringIds.has(auditId)
              ? [auditId]
              : [];
          }),
        ),
      ].sort();
      const reconciledAuthoringAuditIds = authorings
        .filter(
          (authoring) =>
            authoring.projection_warning === "authoring_event_missing" ||
            authoring.projection_state === "reconciled_from_attempt",
        )
        .map((authoring) => authoring.audit_id)
        .sort();
      const final = finalAttempt(linkedAttempts);
      const accepted = linkedAttempts.some(isAccepted);
      const fallback = linkedAttempts.some(
        (attempt) => attempt.fallback || attempt.prompt_audit_summary?.fallback,
      );
      const retried = linkedAttempts.length > 1;
      const missing = activeAuthoring
        ? ["failed", "omitted", "missing_image", "contract_rejected"].includes(
            activeAuthoring.status ?? "",
          )
        : false;
      const routeRole =
        activeAuthoring?.route_role === "primary" ||
        activeAuthoring?.route_role === "secondary"
          ? activeAuthoring.route_role
          : final?.prompt_audit_summary?.route_role === "primary" ||
              final?.prompt_audit_summary?.route_role === "secondary"
            ? final.prompt_audit_summary.route_role
            : "unknown";
      const finalSummary = final?.prompt_audit_summary;
      const requestedModel =
        final?.requested_model ??
        finalSummary?.requested_model ??
        final?.model ??
        null;
      const reportedModel =
        final?.reported_model ??
        finalSummary?.reported_model ??
        final?.model ??
        null;
      const retryReason = retried
        ? (linkedAttempts.find((attempt) => !isAccepted(attempt))?.error_code ??
          linkedAttempts.find((attempt) => !isAccepted(attempt))
            ?.error_message ??
          "earlier provider attempt did not complete")
        : null;
      return {
        sceneId,
        position: activeAuthoring?.scene_position ?? null,
        sourceFile: activeAuthoring?.source_file ?? null,
        routeRole,
        status: fallback
          ? "fallback"
          : accepted
            ? retried
              ? "retried"
              : "accepted"
            : missing
              ? "missing"
              : linkedAttempts.length
                ? "pending"
                : activeAuthoring
                  ? "static"
                  : "pending",
        authorings,
        activeAuthoring,
        missingAuthoringAuditIds,
        reconciledAuthoringAuditIds,
        attempts: linkedAttempts,
        providerAttemptCount: linkedAttempts.length,
        finalModel: final?.model ?? final?.prompt_audit_summary?.model ?? null,
        finalProvider:
          final?.concrete_provider ??
          final?.provider ??
          final?.prompt_audit_summary?.concrete_provider ??
          final?.prompt_audit_summary?.provider ??
          null,
        whyMotion: decisionText(activeAuthoring),
        authoredPrompt: activeAuthoring?.prompt ?? null,
        effectivePromptHash:
          final?.prompt_audit_summary?.positive_prompt_sha256 ?? null,
        correlation: final?.prompt_audit_summary?.authoring_correlation ?? null,
        sceneRevisionId:
          activeAuthoring?.scene_revision_id ??
          final?.scene_revision_id ??
          finalSummary?.scene_revision_id ??
          null,
        promptRevisionId:
          activeAuthoring?.prompt_revision_id ??
          activeAuthoring?.scene_revision_id ??
          final?.scene_revision_id ??
          finalSummary?.scene_revision_id ??
          null,
        configReleaseId:
          activeAuthoring?.config_release_id ??
          finalSummary?.config_release_id ??
          journey?.i2v_prompt_director?.config_release_id ??
          null,
        configReleaseDigest:
          activeAuthoring?.config_release_digest ??
          final?.config_release_digest ??
          finalSummary?.config_release_digest ??
          journey?.i2v_prompt_director?.config_release_digest ??
          null,
        routeContractVersion:
          activeAuthoring?.route_contract_version ??
          final?.route_contract_version ??
          finalSummary?.route_contract_version ??
          journey?.i2v_prompt_director?.route_contract_version ??
          null,
        routeContractDigest:
          activeAuthoring?.route_contract_digest ??
          final?.route_contract_digest ??
          finalSummary?.route_contract_digest ??
          journey?.i2v_prompt_director?.route_contract_digest ??
          null,
        promptBundleId:
          activeAuthoring?.prompt_bundle_id ??
          finalSummary?.prompt_bundle_id ??
          journey?.i2v_prompt_director?.prompt_bundle_id ??
          null,
        promptBundleDigest:
          activeAuthoring?.prompt_bundle_digest ??
          finalSummary?.prompt_bundle_digest ??
          journey?.i2v_prompt_director?.prompt_bundle_digest ??
          null,
        templateId:
          activeAuthoring?.template_id ??
          finalSummary?.template_id ??
          journey?.i2v_prompt_director?.template_id ??
          null,
        templateVersion:
          activeAuthoring?.template_version ??
          finalSummary?.template_version ??
          journey?.i2v_prompt_director?.template_version ??
          null,
        templateDigest:
          activeAuthoring?.template_digest ??
          finalSummary?.template_digest ??
          journey?.i2v_prompt_director?.template_digest ??
          null,
        packetId: final?.packet_id ?? finalSummary?.packet_id ?? null,
        packetDigest:
          final?.packet_digest ?? finalSummary?.packet_digest ?? null,
        providerRequestDigest:
          final?.provider_request_digest ??
          finalSummary?.provider_request_digest ??
          null,
        renderSpecFingerprint:
          final?.render_spec_fingerprint ??
          finalSummary?.render_spec_fingerprint ??
          null,
        requestedModel,
        reportedModel,
        modelMismatch: Boolean(
          requestedModel && reportedModel && requestedModel !== reportedModel,
        ),
        retryReason,
        fallbackReason:
          final?.fallback_from ?? finalSummary?.fallback_from ?? null,
        reuseStatus: final?.reuse_status ?? finalSummary?.reuse_status ?? null,
        reuseReason: final?.reuse_reason ?? finalSummary?.reuse_reason ?? null,
        transformationRecords:
          final?.transformation_records ??
          finalSummary?.transformation_records ??
          [],
        capabilityWarnings:
          final?.capability_warnings ?? finalSummary?.capability_warnings ?? [],
        attemptLineage: linkedAttempts.map((attempt) => ({
          auditId: attempt.audit_id,
          status: attemptStatus(attempt),
          semanticAttemptOrdinal:
            attempt.semantic_attempt_ordinal ??
            attempt.prompt_audit_summary?.semantic_attempt_ordinal ??
            null,
          transportTryIndex:
            attempt.transport_try_index ??
            attempt.prompt_audit_summary?.transport_try_index ??
            null,
          parentAttemptId:
            attempt.parent_attempt_id ??
            attempt.prompt_audit_summary?.parent_attempt_id ??
            null,
          patchId:
            attempt.patch_id ?? attempt.prompt_audit_summary?.patch_id ?? null,
          provider: attempt.concrete_provider ?? attempt.provider ?? null,
          requestedModel:
            attempt.requested_model ??
            attempt.prompt_audit_summary?.requested_model ??
            attempt.model ??
            null,
          reportedModel:
            attempt.reported_model ??
            attempt.prompt_audit_summary?.reported_model ??
            attempt.model ??
            null,
          providerRequestDigest:
            attempt.provider_request_digest ??
            attempt.prompt_audit_summary?.provider_request_digest ??
            null,
          idempotencyKey:
            attempt.idempotency_key ??
            attempt.prompt_audit_summary?.idempotency_key ??
            null,
          correctionValidation:
            attempt.prompt_audit_summary?.correction_validation ?? null,
        })),
      };
    })
    .sort((left, right) => {
      if (left.position != null && right.position != null)
        return left.position - right.position;
      return Number(left.sceneId) - Number(right.sceneId);
    });

  const summary = journey?.i2v_prompt_director;
  const authoringProjectionGapCount = new Set(
    scenes.flatMap((scene) => [
      ...scene.missingAuthoringAuditIds,
      ...scene.reconciledAuthoringAuditIds,
    ]),
  ).size;
  const llmAuthoredSceneCount = new Set(
    scenes
      .filter((scene) =>
        scene.authorings.some((item) => item.status === "authored"),
      )
      .map((scene) => scene.sceneId),
  ).size;
  // "Directed" = the scene has successful prompt provenance, whether the
  // per-model director authored it or the scriptwriter's prompt was copied
  // through (legacy single-prompt pipeline).
  const provenancedSceneCount = new Set(
    scenes
      .filter((scene) =>
        scene.authorings.some((item) =>
          ["authored", "copied_legacy"].includes(item.status ?? ""),
        ),
      )
      .map((scene) => scene.sceneId),
  ).size;
  return {
    telemetryState: authoringProjectionGapCount
      ? "projection_degraded"
      : (journey?.i2v_prompt_telemetry_state ?? "unavailable_legacy"),
    status: authoringProjectionGapCount
      ? "partial"
      : (summary?.status ?? (scenes.length ? "partial" : "unavailable")),
    authoringMode: summary?.authoring_mode ?? null,
    eligibleSceneCount: summary?.scene_count ?? scenes.length,
    attachedSceneCount: summary?.images_attached ?? null,
    authoredSceneCount: summary?.authored_scene_count ?? llmAuthoredSceneCount,
    directedSceneCount:
      summary?.resolved_scene_count ??
      summary?.authored_scene_count ??
      provenancedSceneCount,
    customPromptSceneCount: llmAuthoredSceneCount,
    primarySceneCount:
      summary?.primary_scene_count ??
      scenes.filter((scene) => scene.routeRole === "primary").length,
    secondarySceneCount:
      summary?.secondary_scene_count ??
      scenes.filter((scene) => scene.routeRole === "secondary").length,
    acceptedSceneCount: scenes.filter((scene) =>
      ["accepted", "retried", "fallback"].includes(scene.status),
    ).length,
    retriedSceneCount: scenes.filter((scene) => scene.providerAttemptCount > 1)
      .length,
    fallbackSceneCount: scenes.filter((scene) => scene.status === "fallback")
      .length,
    providerAttemptCount: scenes.reduce(
      (total, scene) => total + scene.providerAttemptCount,
      0,
    ),
    authoringProjectionGapCount,
    visionDigest: summary?.vision_digest ?? null,
    shotListDigest: summary?.shot_list_digest ?? null,
    scenes,
  };
}
